/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class MealsOnWheelsAmerica extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/mow-meals-on-wheels-america", market);
    const alt = "Lasting Smiles";
    const title = "Meals on Wheels America · Partners · Lasting Smiles";
    const description = "Give back to Meals on Wheels America with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/mow-meals-on-wheels-america", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/mow-meals-on-wheels-america", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/mow-meals-on-wheels-america", market),
                    "name": "Meals on Wheels America",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/mow-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/mow-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Meals on Wheels America"
                className="block mx-auto"
                src={require("../../public/images/logos/mow-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Meals on Wheels America
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              Meals on Wheels America is the leadership organization supporting the more than 5,000 community-based programs across the country that are dedicated to addressing senior isolation and hunger.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Meals on Wheels America Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/mow/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Meals on Wheels America Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/mow/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Meals on Wheels America Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/mow/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/mow/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Meals on Wheels America Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/mow/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              Your support of Meals on Wheels America helps this network serve virtually every community in America and, along with more than two million staff and volunteers, deliver the nutritious meals, friendly visits, and safety checks that enable America's seniors to live nourished lives with independence and dignity.
              By providing funding, leadership, education, research, and advocacy support, Meals on Wheels America empowers its local member programs to strengthen their communities, one senior at a time.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              There are now nearly 9.5 million seniors threatened by hunger in America &ndash; like Bertha.
            </h2>
            <p className="text-center">
              Bertha's home was once filled with the constant bustle of her eight children and their non-stop ACTIVITY.
              And, now, even at the age of 95, nothing brings her more joy than spending time with her family when they come to visit.
              However, most days are very lonely for Bertha, now living in an empty house.
              That's why it's so important that the Meals on Wheels volunteers who bring her meals also deliver a friendly hello.
              <br />
              Bertha says, &ldquo;Meals on Wheels allows me to eat well and has been a blessing for the whole family.
              There is a reason Meals on Wheels is so well-known and trusted in virtually every community across the country.
              Simply put, it works!&rdquo;
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Watch how Meals on Wheels America creates lasting smiles for seniors in need.
            </h2>
            <LazyLoad
              className="min-h-200 relative sm:min-h-400 my-5"
              offset={250}
            >
              <iframe
                allowFullScreen={true}
                className="absolute h-full left-0 top-0 w-full"
                src="https://www.youtube.com/embed/vg3neT8yISM"
                title="Your Kindness Sparked a Movement"
              />
            </LazyLoad>
          </div>
          <div className="text-center">
            <Link
              as={toRelativePageURL("/products", market as string)}
              href={{
                pathname: "/products",
                query: { market },
              }}
            >
              <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                Shop &amp; Make a Difference
              </a>
            </Link>
          </div>
        </section>
      </>
    );
  }
}
